/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 화일명 : MfaModal.tsx
// 설명   : 2차인증(MFA) 팝업(공용) - LoginPage 전용(로그인 전 단계라 다른 화면에서는 쓰이지 않음).
//   - stage='enroll' : 아직 2차인증을 등록한 적이 없는 사용자(전체 강제 정책상 최초 1회는 반드시 거침).
//     방식(TOTP 앱 / 이메일) 선택 -> 등록시작(시크릿·QR 또는 코드발송) -> 코드 1회 확인 순서로 진행됨.
//   - stage='verify' : 이미 등록된 사용자의 로그인 검증 - 방식은 서버가 정해둔 값(method)을 그대로 씀.
//   - 두 stage 모두 성공하면 서버가 곧바로 로그인을 완료(userInfo 반환 + 세션쿠키 발급)시켜주므로, 이
//     모달은 그 결과를 그대로 부모(LoginPage)에게 onSuccess로 넘기기만 함(완료 처리는 LoginPage.completeLogin이 함).
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { getClientInfo } from '../utils/clientInfoUtil'; // 로그인 이력용 접속 환경(화면/시간대/언어/플랫폼)
import '../styles/loginPage.css'; // ChangePasswordModal과 같은 오버레이/카드/폼 클래스를 재사용
import { useCommonTranslation } from '../translations';
import { getMsgText } from '../utils/commonUtils';

export interface MfaModalProps {
  open: boolean;
  pendingToken: string;
  stage: 'enroll' | 'verify';
  hasEmail?: boolean;          // enroll: 이메일 방식을 선택지로 보여줄지 / verify(TOTP): OTP 분실 시 이메일 본인확인 재등록을 쓸 수 있는지
  method?: 'TOTP' | 'EMAIL';   // stage='verify'일 때 서버가 정해준 등록된 방식
  allowRemember?: boolean;     // ZA_MFA_POLICY(Z4007M1)의 기기기억 사용여부 - false면 체크박스 자체를 숨김
  rememberDays?: number;       // ZA_MFA_POLICY(Z4007M1)의 기기기억 일수 - 안내 문구에 그대로 표시(하드코딩 금지)
  expireMinutes?: number;      // 이메일 인증코드 유효시간(분, mfaUtil.EMAIL_OTP_EXPIRE_MINUTES) - stage='verify'+EMAIL일 때만 유의미
  onClose: () => void;
  onSuccess: (userInfo: any, tokenExpiresAt: number) => void;
}

type Step = 'choose' | 'code';

// 버튼 라벨이 길어서(예: "OTP 앱으로 등록(Google/MS Authenticator)") 좁은 화면에서 괄호 중간이
// 어색하게 줄바꿈되는 것을 막기 위해, 여는 괄호 "(" 앞에서 강제로 줄바꿈함(언어 무관하게 동작).
function renderWithParenBreak(label: string): React.ReactNode {
  const idx = label.indexOf('(');
  if (idx === -1) return label;
  return <>{label.slice(0, idx).trimEnd()}<br />{label.slice(idx)}</>;
}

const MfaModal: React.FC<MfaModalProps> = ({ open, pendingToken, stage, hasEmail, method, allowRemember = true, rememberDays, expireMinutes: expireMinutesProp, onClose, onSuccess }) => {
  const { ddKey, language } = useCommonTranslation();
  const [step, setStep] = useState<Step>(stage === 'enroll' ? 'choose' : 'code');
  const [chosenMethod, setChosenMethod] = useState<'TOTP' | 'EMAIL'>(method || 'TOTP');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  // 이메일 등록(startEnroll) 시점에는 apiMfaEnrollStart 응답에서 새로 받고, 검증(verify) 시점에는 이미
  // LoginPage가 로그인 응답으로 받아둔 값을 prop으로 그대로 씀 - 둘 다 같은 mfaUtil.EMAIL_OTP_EXPIRE_MINUTES.
  const [expireMinutes, setExpireMinutes] = useState<number | undefined>(expireMinutesProp);
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(allowRemember);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  // OTP 앱 분실 복구(verify+TOTP 전용): 이메일 본인확인 화면(recoverStep='code') -> 통과하면 새 QR로 등록(recovered=true,
  // 이후는 stage='enroll'과 동일하게 동작 - 등록 확정 API가 새 OTP 코드를 확인하면서 로그인까지 완료함).
  const [recoverStep, setRecoverStep] = useState<'none' | 'code'>('none');
  const [recovered, setRecovered] = useState(false);
  const effStage: 'enroll' | 'verify' = recovered ? 'enroll' : stage;

  if (!open) return null;

  // 오답 시 코드 입력칸만 비우고 에러 메세지는 남겨둠 - 이전에는 setError(msg) 직후 이걸 불러서
  // 같은 이벤트 안에서 에러가 바로 ''로 덮어써졌음(React 배치) - 사용자에게는 "코드만 비워지고
  // 아무 반응 없음"으로 보였던 버그(2026-09-27, 실사용자 보고로 발견).
  const resetForNewAttempt = () => { setCode(''); };

  // 등록 시작(방식 선택 직후) - TOTP는 QR/시크릿을, 이메일은 발송 결과를 받아옴
  const startEnroll = async (m: 'TOTP' | 'EMAIL') => {
    setBusy(true);
    setError('');
    try {
      const res = await axios.post(`${API_BASE_URL}/com/apiMfaEnrollStart`, { langCd: language, pendingToken, method: m });
      if (!res.data?.success) {
        setError(res.data?.message || ddKey('msgMfaEnrollStartFailed') || '등록을 시작하지 못했습니다.');
        return;
      }
      setChosenMethod(m);
      if (m === 'TOTP') {
        setQrDataUrl(res.data.qrDataUrl || '');
        setSecret(res.data.secret || '');
      } else {
        setMaskedEmail(res.data.maskedEmail || '');
        setExpireMinutes(res.data.expireMinutes);
      }
      setStep('code');
    } catch (err: any) {
      setError(getMsgText(err, ddKey('msgMfaEnrollStartFailed') || '등록을 시작하지 못했습니다.'));
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setBusy(true);
    setResendMsg('');
    setError('');
    try {
      const res = await axios.post(`${API_BASE_URL}/com/apiMfaResendEmailCode`, { langCd: language, pendingToken });
      setResendMsg(res.data?.success ? (ddKey('msgMfaCodeResent') || '코드를 다시 보냈습니다.') : (res.data?.message || ddKey('msgMfaResendFailed') || '재발송에 실패했습니다.'));
    } catch (err: any) {
      setResendMsg(getMsgText(err, ddKey('msgMfaResendFailed') || '재발송에 실패했습니다.'));
    } finally {
      setBusy(false);
    }
  };

  // OTP 분실 복구 1단계 - 등록된 이메일로 본인확인 코드 발송(재발송도 같은 API)
  const handleRecoverStart = async () => {
    setBusy(true);
    setError('');
    setResendMsg('');
    try {
      const res = await axios.post(`${API_BASE_URL}/com/apiMfaRecoverStart`, { langCd: language, pendingToken, clientInfo: getClientInfo(language) });
      if (!res.data?.success) {
        setError(res.data?.message || ddKey('msgMfaRecoverStartFailed') || '본인확인 메일을 보내지 못했습니다.');
        return;
      }
      setMaskedEmail(res.data.maskedEmail || '');
      setExpireMinutes(res.data.expireMinutes);
      setCode('');
      setRecoverStep('code');
    } catch (err: any) {
      setError(getMsgText(err, ddKey('msgMfaRecoverStartFailed') || '본인확인 메일을 보내지 못했습니다.'));
    } finally {
      setBusy(false);
    }
  };

  // OTP 분실 복구 2단계 - 이메일 코드 확인 후 새 QR(시크릿)을 받아 등록 화면으로 전환
  const handleRecoverConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) {
      setError(ddKey('msgMfaCodeRequired') || '코드를 입력하세요.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await axios.post(`${API_BASE_URL}/com/apiMfaRecoverConfirm`, { langCd: language, pendingToken, code: code.trim(), clientInfo: getClientInfo(language) });
      if (res.data?.success) {
        setQrDataUrl(res.data.qrDataUrl || '');
        setSecret(res.data.secret || '');
        setChosenMethod('TOTP');
        setRecovered(true);
        setRecoverStep('none');
        setResendMsg('');
        setCode('');
        setStep('code');
      } else {
        setError(res.data?.message || ddKey('msgMfaCodeInvalid') || '코드가 올바르지 않습니다.');
        setCode('');
      }
    } catch (err: any) {
      setError(getMsgText(err, ddKey('msgMfaCodeInvalid') || '코드가 올바르지 않습니다.'));
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) {
      setError(ddKey('msgMfaCodeRequired') || '코드를 입력하세요.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const url = effStage === 'enroll' ? '/com/apiMfaEnrollConfirm' : '/com/apiMfaVerify';
      const res = await axios.post(`${API_BASE_URL}${url}`, { langCd: language, pendingToken, code: code.trim(), rememberDevice, clientInfo: getClientInfo(language) }, { withCredentials: true });
      if (res.data?.success && res.data?.tokenExpiresAt) {
        onSuccess(res.data.userInfo, res.data.tokenExpiresAt);
      } else {
        setError(res.data?.message || ddKey('msgMfaCodeInvalid') || '코드가 올바르지 않습니다.');
        resetForNewAttempt();
      }
    } catch (err: any) {
      setError(getMsgText(err, ddKey('msgMfaCodeInvalid') || '코드가 올바르지 않습니다.'));
    } finally {
      setBusy(false);
    }
  };

  const activeMethod = effStage === 'enroll' ? chosenMethod : (method || 'TOTP');

  return (
    <div className="pwd-change-overlay">
      <div className="pwd-change-card">
        <h3 className="login-title">{ddKey('lblMfaTitle') || '2차인증'}</h3>

        {effStage === 'enroll' && step === 'choose' && (
          <>
            <p className="pwd-change-desc">{ddKey('msgMfaEnrollDesc') || '처음 로그인하시는군요. 2차인증 방식을 선택해주세요(최초 1회 등록).'}</p>
            {error && <p className="pwd-change-error">{error}</p>}
            <div className="form-group pwd-change-actions" style={{ flexDirection: 'column', gap: 10 }}>
              <button type="button" className="login-btn" disabled={busy} onClick={() => startEnroll('TOTP')}>
                {renderWithParenBreak(ddKey('btnMfaChooseTotp') || 'OTP 앱으로 등록(Google/MS Authenticator)')}
              </button>
              {/* 이메일 등록 버튼 - hasEmail이 false(등록된 이메일 없음)여도 버튼 자체는 항상 보여주고
                  비활성화만 시킴(존재는 알되 왜 못 쓰는지 알 수 있게). 호버로만 보이는 title 툴팁 대신,
                  바로 아래에 이유를 항상 텍스트로 노출함(터치 환경 등에서도 바로 보이도록). */}
              <button
                type="button"
                className="login-btn"
                disabled={busy || !hasEmail}
                onClick={() => startEnroll('EMAIL')}
              >
                {ddKey('btnMfaChooseEmail') || '이메일로 등록'}
              </button>
              {!hasEmail && (
                <p className="pwd-change-hint" style={{ margin: '-4px 0 0' }}>
                  {ddKey('msgMfaNoEmailRegistered') || '등록된 이메일이 없어 사용할 수 없습니다.'}
                </p>
              )}
              <button type="button" className="pwd-change-cancel-btn" disabled={busy} onClick={onClose}>
                {ddKey('btnCancel') || '취소'}
              </button>
            </div>
          </>
        )}

        {recoverStep === 'code' && (
          <>
            <p className="pwd-change-desc">
              {(ddKey('msgMfaRecoverDesc') || '{email}로 본인확인 코드를 보냈습니다. 코드를 확인하면 OTP를 새로 등록합니다.').replace('{email}', maskedEmail)}
            </p>
            <form className="login-form" onSubmit={handleRecoverConfirm}>
              <div className="form-group">
                <label htmlFor="mfaRecoverCode" className="form-label">{ddKey('lblMfaCode') || '인증코드'}</label>
                <input
                  id="mfaRecoverCode"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  className="form-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  disabled={busy}
                  autoFocus
                  maxLength={6}
                />
              </div>
              <button type="button" className="pwd-change-cancel-btn" disabled={busy} onClick={handleRecoverStart}>
                {ddKey('btnMfaResend') || '코드 재발송'}
              </button>
              {!!expireMinutes && (
                <p className="pwd-change-hint">
                  {(ddKey('msgMfaEmailExpireHint') || '{min}분 이내에 입력해주세요.').replace('{min}', String(expireMinutes))}
                </p>
              )}
              {error && <p className="pwd-change-error">{error}</p>}
              <div className="form-group pwd-change-actions">
                <button type="submit" className={`login-btn ${busy ? 'loading' : ''}`} disabled={busy}>
                  {busy ? (ddKey('loading') || '처리중...') : (ddKey('btnMfaConfirm') || '확인')}
                </button>
                <button type="button" className="pwd-change-cancel-btn" disabled={busy} onClick={() => { setRecoverStep('none'); setCode(''); setError(''); }}>
                  {ddKey('btnCancel') || '취소'}
                </button>
              </div>
            </form>
          </>
        )}

        {step === 'code' && recoverStep === 'none' && (
          <>
            {effStage === 'enroll' && activeMethod === 'TOTP' && (
              <>
                <p className="pwd-change-desc">{ddKey('msgMfaTotpScanDesc') || 'OTP 앱(Google/MS Authenticator 등)으로 아래 QR코드를 스캔한 뒤, 앱에 표시된 6자리 코드를 입력하세요.'}</p>
                {qrDataUrl && <img src={qrDataUrl} alt="TOTP QR" style={{ display: 'block', margin: '0 auto 10px', width: 180, height: 180 }} />}
                {secret && (
                  <p className="pwd-change-hint">
                    {ddKey('msgMfaTotpManualEntry') || 'QR을 스캔할 수 없다면 이 키를 직접 입력하세요'}: <code>{secret}</code>
                  </p>
                )}
              </>
            )}
            {effStage === 'enroll' && activeMethod === 'EMAIL' && (
              <p className="pwd-change-desc">
                {(ddKey('msgMfaEmailSentDesc') || '{email}로 인증코드를 보냈습니다. 메일함을 확인해주세요.').replace('{email}', maskedEmail)}
              </p>
            )}
            {effStage === 'verify' && (
              <p className="pwd-change-desc">
                {activeMethod === 'TOTP'
                  ? (ddKey('msgMfaVerifyTotpDesc') || 'OTP 앱에 표시된 6자리 코드를 입력하세요.')
                  : (ddKey('msgMfaVerifyEmailDesc') || '등록된 이메일로 보낸 인증코드를 입력하세요.')}
              </p>
            )}

            <form className="login-form" onSubmit={handleSubmitCode}>
              <div className="form-group">
                <label htmlFor="mfaCode" className="form-label">{ddKey('lblMfaCode') || '인증코드'}</label>
                <input
                  id="mfaCode"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  className="form-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  disabled={busy}
                  autoFocus
                  maxLength={6}
                />
              </div>
              {error && <p className="pwd-change-error">{error}</p>}
              {allowRemember && (
                // 기기기억 여부는 정책(ZA_MFA_POLICY)에 따라 자동으로 적용됨 - 사용자가 껐다 켰다 할 수 있는
                // 항목이 아니라 그냥 보여주기만 함(체크 자체를 못 하도록 disabled, 값은 항상 true로 고정).
                <div className="form-group" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', alignItems: 'center', gap: 8 }}>
                  <input id="mfaRemember" type="checkbox" checked readOnly disabled style={{ flexShrink: 0 }} />
                  <label htmlFor="mfaRemember" className="form-label" style={{ margin: 0, whiteSpace: 'nowrap' }}>
                    {rememberDays
                      ? (ddKey('lblMfaRememberDeviceDays') || '{days}일간 다시 묻지 않습니다.').replace('{days}', String(rememberDays))
                      : (ddKey('lblMfaRememberDevice') || '다시 묻지 않습니다.')}
                  </label>
                </div>
              )}
              {activeMethod === 'EMAIL' && (
                <button type="button" className="pwd-change-cancel-btn" disabled={busy} onClick={handleResend}>
                  {ddKey('btnMfaResend') || '코드 재발송'}
                </button>
              )}
              {resendMsg && <p className="pwd-change-hint">{resendMsg}</p>}
              {/* OTP 앱 분실 복구 - 비밀번호만으로는 재생성할 수 없고, 등록된 이메일로 본인확인을 거쳐야 함.
                  이메일이 없으면 버튼은 보여주되 비활성화하고 이유(관리자 초기화 안내)를 텍스트로 노출. */}
              {effStage === 'verify' && activeMethod === 'TOTP' && (
                <>
                  <button type="button" className="pwd-change-cancel-btn" style={{ marginBottom: 8 }} disabled={busy || !hasEmail} onClick={handleRecoverStart}>
                    {ddKey('btnMfaLostOtp') || 'OTP 앱을 분실하셨나요? (이메일로 재등록)'}
                  </button>
                  {!hasEmail && (
                    <p className="pwd-change-hint" style={{ margin: '-4px 0 0' }}>
                      {ddKey('msgMfaLostOtpNoEmail') || '등록된 이메일이 없어 재등록할 수 없습니다. 관리자에게 2차인증 초기화를 요청하세요.'}
                    </p>
                  )}
                </>
              )}
              {activeMethod === 'EMAIL' && !!expireMinutes && (
                <p className="pwd-change-hint">
                  {(ddKey('msgMfaEmailExpireHint') || '{min}분 이내에 입력해주세요.').replace('{min}', String(expireMinutes))}
                </p>
              )}
              <div className="form-group pwd-change-actions">
                <button type="submit" className={`login-btn ${busy ? 'loading' : ''}`} disabled={busy}>
                  {busy ? (ddKey('loading') || '처리중...') : (ddKey('btnMfaConfirm') || '확인')}
                </button>
                <button type="button" className="pwd-change-cancel-btn" disabled={busy} onClick={onClose}>
                  {ddKey('btnCancel') || '취소'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default MfaModal;
